import { useState, useEffect, useCallback } from 'react';
import { authenticatedFetch } from '@/utils/api';
import { API_URLS } from '@/constants';
import { UsageCounter, UsageWithPercentage, FeatureKey } from '@/types/subscription';
import { useAuth } from '@/contexts';

const PERCENTAGE_MULTIPLIER = 100;
const MIN_USAGE_VALUE = 0;

/**
 * Hook to fetch and manage usage limits for the current user
 */
export function useUsageLimits() {
  const { isLoggedIn } = useAuth();
  const [usage, setUsage] = useState<UsageWithPercentage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsageLimits = useCallback(async () => {
    if (!isLoggedIn) {
      setUsage([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(API_URLS.USAGE.LIMITS);
      
      if (!response.ok) {
       
        setUsage([]);
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      
      if (data.success && data.usage) {
        // Transform usage data to include calculated fields
        const transformedUsage: UsageWithPercentage[] = data.usage.map((counter: UsageCounter) => {
          const isUnlimited = counter.limit_value === null;
          const percentage_used = isUnlimited 
            ? null 
            : (counter.limit_value ?? 0) > MIN_USAGE_VALUE
              ? Math.round((counter.current_usage / (counter.limit_value ?? 0)) * PERCENTAGE_MULTIPLIER) 
              : MIN_USAGE_VALUE;
          const remaining = isUnlimited 
            ? null 
            : Math.max(MIN_USAGE_VALUE, (counter.limit_value ?? 0) - counter.current_usage);
          const hasCapacity = isUnlimited ? true : counter.current_usage < (counter.limit_value ?? 0);

          return {
            ...counter,
            isUnlimited,
            percentage_used,
            remaining,
            hasCapacity
          };
        });

        setUsage(transformedUsage);
      } else {
        setUsage([]);
      }
    } catch (err) {
      // Silently fail - don't break the UI
      console.error('Failed to fetch usage limits:', err);
      setError(null); // Don't show error to user
      setUsage([]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchUsageLimits();
  }, [fetchUsageLimits]);

  // Listen for usage update events (e.g., when prompt visibility changes)
  useEffect(() => {
    const handleUsageUpdate = () => {
      fetchUsageLimits();
    };

    window.addEventListener('usageUpdated', handleUsageUpdate);
    
    return () => {
      window.removeEventListener('usageUpdated', handleUsageUpdate);
    };
  }, [fetchUsageLimits]);

  return {
    usage,
    isLoading,
    error,
    refetch: fetchUsageLimits
  };
}

/**
 * Hook to get usage for a specific feature
 */
export function useFeatureUsage(featureKey: FeatureKey): UsageWithPercentage | null {
  const { usage, isLoading } = useUsageLimits();

  if (isLoading || usage.length === 0) {
    return null;
  }

  return usage.find(u => u.feature_key === featureKey) || null;
}

